// ./src/store/index.ts

import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import userReducer, { setLoading } from './slices/userSlice';
import invitationReducer, { setIsSaveEnabled, selectProjectChanges } from './slices/invitationSlice';
import designReducer from './slices/designSlice';
import bitmapReducer from './slices/bitmapSlice';
import contactsReducer from './slices/contactSlice';
import navigationReducer from './slices/navigationSlice';
import historyReducer from './slices/historySlice';
import { useDispatch } from 'react-redux';
import { ThunkAction, Action } from '@reduxjs/toolkit';

// Noop storage for non-browser environments (like SSR)
const noopStorage = {
  getItem: () => Promise.resolve(null),
  setItem: () => Promise.resolve(),
  removeItem: () => Promise.resolve(),
};

// Check for browser environment
const isBrowser = typeof window !== 'undefined';

// Persist configuration
const persistConfig = {
  key: 'root',
  storage: isBrowser ? storage : noopStorage,
  whitelist: ['user', 'invitation', 'design', 'bitmap', 'contacts'], // Only persist these reducers
};

// Combine the reducers first
const rootReducer = combineReducers({
  user: userReducer,
  invitation: invitationReducer,
  design: designReducer,
  bitmap: bitmapReducer,
  contacts: contactsReducer,
  navigation: navigationReducer,
  history: historyReducer,
});

// Apply persistReducer to rootReducer in browser environment
const persistedReducer = isBrowser ? persistReducer(persistConfig, rootReducer) : rootReducer;

// Configure the store
// @ts-ignore
export const store = configureStore({
  reducer: persistedReducer as any, // <-- Explicitly cast reducer to bypass type conflicts
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }) as any, // <-- Explicitly cast middleware to bypass type conflicts
});

// Only persist store in the browser
export const persistor = isBrowser
  ? persistStore(store, null, () => {
      if (store.dispatch) {
        store.dispatch(setLoading(false)); // Ensure store.dispatch is defined
      }
    })
  : null;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch & {
  <ReturnType = void>(asyncAction: ThunkAction<ReturnType, RootState, unknown, Action<string>>): ReturnType;
};
export const useAppDispatch = () => useDispatch<AppDispatch>();
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
